import type { GitSummaryResponse } from "../nestAPIs/git"
import { gitApi } from "../nestAPIs/git"
import { HARNESS_META, harnessIdForProvider } from "../modelRegistry"
import type { WildLarkTaskSessionsListResult, WildLarkTaskWorktree } from "../wildlark/client"

const CHECKOUT_SUMMARY_CONCURRENCY = 4
const MAX_REPORTED_CHECKOUT_ERRORS = 3

type TaskCheckoutGitReader = Pick<typeof gitApi, "getGitSummary" | "getCommitCount" | "listBranches">

interface TaskCheckoutCandidate {
    path: string
    worktreeId?: string
    manifestBranch?: string
    isPrimary: boolean
    isDesignated: boolean
    auxiliaryLabel?: string
}

export interface TaskCheckoutSummary {
    path: string
    worktreeId?: string
    branch: string | null
    headCommit: string
    isPrimary: boolean
    isDesignated: boolean
    auxiliaryLabel?: string
    commitsAheadOfBase: number | null
    status: GitSummaryResponse | null
    error?: string
}

export interface TaskCheckoutSummaryReadStats {
    status: "complete" | "done-with-gaps"
    requested: number
    succeeded: number
    failed: number
    firstErrors: string[]
}

export interface TaskCheckoutSummaryResult {
    baseBranch: string | null
    checkouts: TaskCheckoutSummary[]
    reads: TaskCheckoutSummaryReadStats
}

interface LoadTaskCheckoutSummariesParams {
    workspaceId: string
    primaryPath: string
    selectedPath?: string
    designatedPath?: string
    manifest: WildLarkTaskSessionsListResult
}

function normalizePath(path: string): string {
    const normalized = path.replace(/\\/g, "/").replace(/\/+/g, "/")
    const withoutTrailingSlash = normalized.length > 1 ? normalized.replace(/\/+$/g, "") : normalized
    return /^[A-Za-z]:\//.test(withoutTrailingSlash) ? withoutTrailingSlash.toLowerCase() : withoutTrailingSlash
}

function samePath(left: string | undefined, right: string | undefined): boolean {
    return !!left && !!right && normalizePath(left) === normalizePath(right)
}

function worktreeForPath(worktrees: WildLarkTaskWorktree[], path: string): WildLarkTaskWorktree | undefined {
    return worktrees.find((worktree) => samePath(worktree.path || worktree.worktreeId, path))
}

function checkoutCandidates(params: LoadTaskCheckoutSummariesParams): TaskCheckoutCandidate[] {
    const repositoryWorktrees = params.manifest.repository?.worktrees ?? []
    const primaryPath = params.manifest.repository?.root || params.primaryPath
    const candidates: TaskCheckoutCandidate[] = []
    const seen = new Set<string>()

    const addCandidate = (path: string | undefined, worktree?: WildLarkTaskWorktree) => {
        if (!path) return
        const key = normalizePath(path)
        if (!key || seen.has(key)) return
        seen.add(key)
        const isPrimary = samePath(path, primaryPath)
        const isDesignated = samePath(path, params.designatedPath ?? params.manifest.designatedWorktree)
        const sessions = params.manifest.sessions.filter((session) => {
            const directory = normalizePath(session.executionDirectory || session.worktreeId || session.cwd || "")
            return directory === key || directory.startsWith(`${key}/`)
        })
        const auxiliaryLabels =
            !isPrimary && !isDesignated && sessions.length > 0 && sessions.every((session) => session.role === "auxiliary")
                ? sessions.map((session) => {
                      const harnessId = harnessIdForProvider(session.providerId)
                      const provider = harnessId ? HARNESS_META[harnessId].name : session.providerId
                      return [session.label || "Agent", provider].filter(Boolean).join(" · ")
                  })
                : []
        candidates.push({
            path,
            ...(isPrimary ? {} : { worktreeId: worktree?.worktreeId || path }),
            ...(worktree?.branch ? { manifestBranch: worktree.branch.replace(/^refs\/heads\//, "") } : {}),
            isPrimary,
            isDesignated,
            ...(auxiliaryLabels.length > 0 ? { auxiliaryLabel: [...new Set(auxiliaryLabels)].join(", ") } : {}),
        })
    }

    addCandidate(primaryPath)
    const designatedPath = params.designatedPath ?? params.manifest.designatedWorktree
    const designatedWorktree = designatedPath ? worktreeForPath(repositoryWorktrees, designatedPath) : undefined
    if (designatedPath && designatedWorktree) addCandidate(designatedPath, designatedWorktree)
    const selectedPath = params.selectedPath ?? params.manifest.selectedWorkingDirectory
    const selectedWorktree = selectedPath ? worktreeForPath(repositoryWorktrees, selectedPath) : undefined
    if (selectedPath && (samePath(selectedPath, primaryPath) || selectedWorktree)) addCandidate(selectedPath, selectedWorktree)
    for (const worktree of repositoryWorktrees) addCandidate(worktree.path || worktree.worktreeId, worktree)

    return candidates
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error)
}

async function mapWithConcurrency<T, R>(items: T[], concurrency: number, worker: (item: T) => Promise<R>): Promise<R[]> {
    if (items.length === 0) return []
    const results: R[] = []
    let nextIndex = 0
    const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
        while (nextIndex < items.length) {
            const index = nextIndex
            nextIndex++
            results[index] = await worker(items[index])
        }
    })
    await Promise.all(workers)
    return results
}

export async function loadTaskCheckoutSummaries(
    params: LoadTaskCheckoutSummariesParams,
    reader: TaskCheckoutGitReader = gitApi
): Promise<TaskCheckoutSummaryResult> {
    const candidates = checkoutCandidates(params)
    const primaryPath = params.manifest.repository?.root || params.primaryPath
    const basePromise = reader
        .listBranches({
            workspaceId: params.workspaceId,
            workspaceRoot: primaryPath,
            directory: primaryPath,
            includeRemote: false,
        })
        .then((value) => ({ ok: true as const, value }))
        .catch((error: unknown) => ({ ok: false as const, error }))

    const checkouts = await mapWithConcurrency(candidates, CHECKOUT_SUMMARY_CONCURRENCY, async (candidate): Promise<TaskCheckoutSummary> => {
        const target = {
            workspaceId: params.workspaceId,
            workspaceRoot: candidate.path,
            directory: candidate.path,
            ...(candidate.worktreeId ? { worktreeId: candidate.worktreeId } : {}),
        }
        let status: GitSummaryResponse
        try {
            status = await reader.getGitSummary(target)
        } catch (error) {
            return {
                path: candidate.path,
                ...(candidate.worktreeId ? { worktreeId: candidate.worktreeId } : {}),
                branch: candidate.manifestBranch ?? null,
                headCommit: "",
                isPrimary: candidate.isPrimary,
                isDesignated: candidate.isDesignated,
                ...(candidate.auxiliaryLabel ? { auxiliaryLabel: candidate.auxiliaryLabel } : {}),
                commitsAheadOfBase: null,
                status: null,
                error: `Git summary unavailable: ${errorMessage(error)}`,
            }
        }

        const base = await basePromise
        if (!base.ok || !base.value.defaultBranchSha) {
            const reason = base.ok ? "the local default branch is unavailable" : errorMessage(base.error)
            return {
                path: candidate.path,
                ...(candidate.worktreeId ? { worktreeId: candidate.worktreeId } : {}),
                branch: status.branch,
                headCommit: status.headCommit,
                isPrimary: candidate.isPrimary,
                isDesignated: candidate.isDesignated,
                ...(candidate.auxiliaryLabel ? { auxiliaryLabel: candidate.auxiliaryLabel } : {}),
                commitsAheadOfBase: null,
                status,
                error: `Commit count unavailable: ${reason}`,
            }
        }

        try {
            const commitsAheadOfBase =
                status.headCommit === base.value.defaultBranchSha
                    ? 0
                    : await reader.getCommitCount({
                          ...target,
                          from: base.value.defaultBranchSha,
                          to: status.headCommit,
                      })
            return {
                path: candidate.path,
                ...(candidate.worktreeId ? { worktreeId: candidate.worktreeId } : {}),
                branch: status.branch,
                headCommit: status.headCommit,
                isPrimary: candidate.isPrimary,
                isDesignated: candidate.isDesignated,
                ...(candidate.auxiliaryLabel ? { auxiliaryLabel: candidate.auxiliaryLabel } : {}),
                commitsAheadOfBase,
                status,
            }
        } catch (error) {
            return {
                path: candidate.path,
                ...(candidate.worktreeId ? { worktreeId: candidate.worktreeId } : {}),
                branch: status.branch,
                headCommit: status.headCommit,
                isPrimary: candidate.isPrimary,
                isDesignated: candidate.isDesignated,
                ...(candidate.auxiliaryLabel ? { auxiliaryLabel: candidate.auxiliaryLabel } : {}),
                commitsAheadOfBase: null,
                status,
                error: `Commit count unavailable: ${errorMessage(error)}`,
            }
        }
    })

    const base = await basePromise
    const failedCheckouts = checkouts.filter((checkout) => checkout.error)
    return {
        baseBranch: base.ok ? base.value.defaultBranch || null : null,
        checkouts,
        reads: {
            status: failedCheckouts.length > 0 ? "done-with-gaps" : "complete",
            requested: checkouts.length,
            succeeded: checkouts.length - failedCheckouts.length,
            failed: failedCheckouts.length,
            firstErrors: failedCheckouts.slice(0, MAX_REPORTED_CHECKOUT_ERRORS).map((checkout) => `${checkout.path}: ${checkout.error}`),
        },
    }
}
